import { IAnswersData } from "@features/shared/entities/onboarding";

export type RouteContext =
    | {
        kind: "onboarding";
        onboardingId: string;
        step: number;
        hasStepSegment: boolean;
        search: string;
    }
    | {
        kind: "experiment";
        step: number;
        hasStepSegment: boolean;
        search: string;
    };

export const extractStepFromParam = (param: string | string[] | undefined) => {
    if (!param) {
        return { step: 0, hasSegment: false };
    }
    const value = Array.isArray(param) ? param[0] : param;
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed >= 0) {
        return { step: parsed, hasSegment: true };
    }
    return { step: 0, hasSegment: false };
};

export const parseRouteContext = ({
    path,
    params,
    search
}: {
    path: string;
    params: Record<string, string | string[]>;
    search: string;
}): RouteContext | null => {
    if (!path) return null;

    if (path.startsWith("/experiment/success-payment") || path.startsWith("/experiment/fail-payment")) {
        return null;
    }

    if (path.startsWith("/onboarding/")) {
        const onboardingParam = params?.onboardingId;
        if (typeof onboardingParam !== "string" || onboardingParam.length === 0) return null;
        const { step, hasSegment } = extractStepFromParam(params?.step);
        return {
            kind: "onboarding",
            onboardingId: onboardingParam,
            step,
            hasStepSegment: hasSegment,
            search
        };
    }

    if (path.startsWith("/experiment")) {
        const { step, hasSegment } = extractStepFromParam(params?.step);
        return {
            kind: "experiment",
            step,
            hasStepSegment: hasSegment,
            search
        };
    }

    return null;
};

export const buildNavigationPayload = (ctx: RouteContext, step: number) => {
    const search = ctx.search ? `?${ctx.search}` : "";
    if (ctx.kind === "onboarding") {
        const base = `/onboarding/${ctx.onboardingId}`;
        const suffix = step > 0 ? `/${step}` : "";
        const url = `${base}${suffix}${search}`;
        const replace = step === 0;
        return { url, replace };
    }

    const base = "/experiment";
    const suffix = step > 0 ? `/${step}` : "";
    const url = `${base}${suffix}${search}`;
    const replace = step === 0;
    return { url, replace };
};

export const SESSION_QUERY_KEY = "session_id";

const normalizeSearch = (search: string) => {
    if (!search) return "";
    return search.startsWith("?") ? search.slice(1) : search;
};

export const getSessionIdFromSearch = (search: string) => {
    const normalized = normalizeSearch(search);
    if (!normalized) return null;
    const params = new URLSearchParams(normalized);
    return params.get(SESSION_QUERY_KEY);
};

export const buildUrlWithSessionId = (path: string, search: string, sessionId: string | null) => {
    const params = new URLSearchParams(normalizeSearch(search));
    if (sessionId) {
        params.set(SESSION_QUERY_KEY, sessionId);
    } else {
        params.delete(SESSION_QUERY_KEY);
    }
    const query = params.toString();
    return query ? `${path}?${query}` : path;
};

export const shouldResetSession = (error: unknown) => {
    if (!error) return false;
    const message = (typeof error === "string" ? error : (error as any)?.error || (error as any)?.message || "")
        .toString()
        .toLowerCase();
    return (
        message.includes("session expired") ||
        message.includes("not found") ||
        message.includes("already completed") ||
        message.includes("expired")
    );
};

export const findAnswerValueById = (answers: IAnswersData[], targetId: string) => {
    for (const answer of answers) {
        if (Array.isArray(answer.data)) {
            const option = answer.data.find((option) => option.id === targetId);
            if (option?.value) return option.value;
        } else if (answer.data?.id === targetId && answer.data.value) {
            return answer.data.value;
        }
    }

    return null;
};